import clsx from 'clsx';
import Field from 'components/Form/Field';
import FieldLabel from 'components/Form/Field/FieldLabel';
import Information from 'components/Information';
import KeyValue from 'components/Layout/KeyValue';
import LinkButton from 'components/LinkButton';
import SectionHeader from 'components/SectionHeader';
import Value from 'components/Value';
import InboxLayout from 'pages/Portal/components/Inbox/InboxLayout';
import InboxTableLayout from 'pages/Portal/components/Inbox/TableLayout';
import Stroke from 'pages/Portal/components/Stroke';
import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';

import {PortalPermissions} from '../../components/Inbox/types';

type ClientInboxUsersViewBackendData = {
    clientName: string;
    clientUsers: string[];
    email: string;
    partnerName: string;
    permissions: PortalPermissions;
    portalMid: string;
    sessionExpiration: number;
};

export type ClientInboxUsersViewBackend =
    Backend<ClientInboxUsersViewBackendData>;

const ClientInboxUsersView: FC = () => {
    const backendData = useBackend<ClientInboxUsersViewBackend>();
    const {data, flags} = backendData;
    const {t} = useTranslation('Portal');
    const users: string[] = [...data.clientUsers];

    const tooltipProps = {
        href: 'https://help.makeleaps.jp/ja/articles/8687198-makeleapsから書類を送付する方法#h_bc10350fef',
        message: t('clientInboxUsers.fieldTooltip'),
    };

    const fieldLabel = (
        <FieldLabel isSideBySide={true} tooltip={tooltipProps}>
            {t('clientInboxUsers.fieldLabel')}
        </FieldLabel>
    );

    return (
        <InboxLayout
            clientName={data.clientName}
            email={data.email}
            flags={flags}
            navigation="client-inbox-users"
            partnerName={data.partnerName}
            permissions={data.permissions}
            portalMid={data.portalMid}
            sessionExpiration={data.sessionExpiration}
        >
            <SectionHeader
                level={1}
                subtitle={t('clientInboxUsers.viewSubtitle', {
                    partnerName: data.partnerName,
                })}
                title={t('clientInboxUsers.viewTitle')}
            />
            <InboxTableLayout>
                <div className="mb-2 flex w-full justify-between">
                    <div className="flex">
                        <Stroke />
                        <div className="min-w-0 whitespace-break-spaces break-words text-base font-semibold text-grey-800">
                            {t('clientInboxUsers.cardTitle')}
                        </div>
                    </div>
                    {data.permissions.hasEditAccessToClientInboxUsers && (
                        <LinkButton
                            aria-label="edit"
                            className="flex"
                            href={`/portal/${data.portalMid}/client-inbox/users/edit`}
                            kind="secondary"
                            size="sm"
                        >
                            {t('edit')}
                        </LinkButton>
                    )}
                </div>
                <div
                    className={clsx(
                        data.permissions.hasEditAccessToClientInboxUsers
                            ? 'mb-20'
                            : ' mt-5',
                        'flex w-[62rem] flex-col',
                    )}
                >
                    <KeyValue className="ml-3">
                        <Field label={fieldLabel} type="value">
                            {users.map((user) => (
                                <Value
                                    className="text-base"
                                    key={user}
                                    value={{
                                        type: 'string',
                                        value: user,
                                    }}
                                />
                            ))}
                        </Field>
                    </KeyValue>
                    {!data.permissions.hasEditAccessToClientInboxUsers && (
                        <Information className="mt-4" icon="info" type="system">
                            {t('clientInboxUsers.information', {
                                partnerName: data.partnerName,
                            })}
                        </Information>
                    )}
                </div>
            </InboxTableLayout>
        </InboxLayout>
    );
};

export default ClientInboxUsersView;
